'use client';

import React from 'react';
import type { Cornerstone, WordpressPost } from '@benzinga/content-manager';
import { Icon } from '@benzinga/core-ui';
import { ShareButtons } from '@benzinga/ui';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faListUl } from '@fortawesome/pro-solid-svg-icons/faListUl';
import { LearnSectionLabel } from './LearnSectionLabel';
import styles from './Learn.module.css';

const SHARE_BUTTON_STYLE = {
  bgStyle: { fill: '#192940' },
  borderRadius: 12,
  iconFillColor: '#ceddf2',
  size: 30,
};

const MARKER = 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[5px] text-[0.625rem] text-blue';

export const LearnSyllabus: React.FC<{ post: WordpressPost; syllabus: Cornerstone }> = ({ post, syllabus }) => {
  const lessons = Array.isArray(syllabus.child_posts) ? syllabus.child_posts : [];
  const activeIndex = lessons.findIndex(lesson => lesson.id === syllabus.current_post_id);
  const shareUrl = post.canonical_link ?? '';
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <nav aria-label="Course syllabus">
      <button
        aria-expanded={isOpen}
        className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border border-white/10 bg-bzblue-1100 px-4 py-3 text-bzblue-300 lg:hidden"
        onClick={() => setIsOpen(open => !open)}
        type="button"
      >
        <Icon className="shrink-0 text-sm text-bzblue-500" icon={faListUl} />
        <span className="flex-1 text-left text-[0.9375rem] font-semibold">Course Syllabus</span>
        <Icon className={`shrink-0 text-sm text-bzblue-500 ${isOpen ? 'rotate-180' : ''}`} icon={faChevronDown} />
      </button>
      <LearnSectionLabel className="mb-3 hidden lg:block" label="Course Syllabus" />
      <div className={isOpen ? 'block' : 'hidden lg:block'}>
        <ol className="m-0 list-none p-0">
          {lessons.map((lesson, index) => {
            const isActive = index === activeIndex;
            const isDone = activeIndex > -1 && index < activeIndex;
            return (
              <li key={`${lesson.id}-${index}`}>
                <a
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex items-center gap-2.5 py-2 text-[0.9375rem] ${
                    isActive ? 'font-semibold text-bzblue-600' : 'text-bzblue-400 hover:text-white'
                  }`}
                  href={lesson.link}
                >
                  <span
                    className={`${MARKER} ${isActive ? 'bg-[#3F83F8]' : isDone ? 'bg-[#3F83F81A]' : 'border border-bzblue-900'}`}
                  >
                    {isDone ? (
                      <Icon icon={faCheck} style={{ color: '#3F83F8' }} />
                    ) : (
                      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-white' : 'bg-bzblue-600'}`} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">{lesson.title}</span>
                </a>
              </li>
            );
          })}
        </ol>
        <div className={`${styles.shareRow} mt-4 flex flex-wrap items-center gap-2.5`}>
          <LearnSectionLabel label="Share" />
          <ShareButtons
            buttonLocation="learn_syllabus"
            buttonStyle={SHARE_BUTTON_STYLE}
            showButtons={['linkedin', 'twitter', 'facebook', 'copy']}
            title={post.title}
            url={shareUrl || undefined}
          />
        </div>
      </div>
    </nav>
  );
};

export default LearnSyllabus;
